import { useState } from 'react'
import { AlertTriangle, ChevronDown, Copy, Pencil, RotateCcw, Trash2 } from 'lucide-react'
import type { Entry } from '@/types/domain'
import {
  formatBRL,
  formatOrig,
  formatRelativeAgo,
  formatStylizedDate,
  weekdayLong,
  weekdayShort,
} from '../format'
import { Badge } from '../shadcn/badge'
import { Button } from '../shadcn/button'

export interface EntryRowProps {
  entry: Entry
  /** Stable display number for this entry across the year-base. */
  numero: number
  showTipo?: boolean
  onEdit?: (entry: Entry) => void
  onCopy?: (entry: Entry) => void
  onRecalc?: (entry: Entry) => void
  onDelete?: (entry: Entry) => void
}

const ROW_GRID =
  'grid grid-cols-[48px_92px_40px_72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_60px_128px] gap-x-4 items-center'
const ROW_GRID_WITH_TIPO =
  'grid grid-cols-[48px_64px_92px_40px_72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_60px_128px] gap-x-4 items-center'

export function EntryRow({
  entry,
  numero,
  showTipo,
  onEdit,
  onCopy,
  onRecalc,
  onDelete,
}: EntryRowProps) {
  const [expanded, setExpanded] = useState(false)
  const isPendente = entry.snapshot.status !== 'calculado'
  const numeroLabel = String(numero).padStart(3, '0')

  return (
    <div
      className={[
        'border-b border-border last:border-b-0 transition-colors group',
        expanded ? 'bg-muted' : 'hover:bg-muted/70',
        isPendente ? 'bg-warning/5' : '',
      ].join(' ')}
    >
      <div className="px-6 py-3">
        <div className={showTipo ? ROW_GRID_WITH_TIPO : ROW_GRID}>
          <span className="text-muted-foreground font-mono text-[12px] select-none">
            {numeroLabel}
          </span>
          {showTipo ? (
            <Badge
              variant={entry.tipo === 'rendimento' ? 'success' : 'destructive'}
              className="justify-center font-mono text-[10px] uppercase tracking-wider select-none"
            >
              {entry.tipo === 'rendimento' ? 'rend' : 'imp'}
            </Badge>
          ) : null}
          <span className="font-mono text-[13px] select-text">
            {formatStylizedDate(entry.dataRecebimento)}
          </span>
          <span
            aria-label="Dia da semana"
            className="text-muted-foreground font-mono text-[11px] lowercase select-text"
          >
            {weekdayShort(entry.dataRecebimento)}
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[13px] select-text">
            {entry.moedaOrigem}
            {isPendente ? (
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
            ) : null}
          </span>
          <span className="text-right font-mono text-[13px] select-text">
            {formatOrig(entry.valorOriginal)}
          </span>
          <span className="text-right font-mono text-[13px] text-muted-foreground select-text">
            {formatOrig(entry.snapshot.valorUSD)}
          </span>
          <span
            className={[
              'text-right font-mono text-[14px] font-medium select-text',
              isPendente ? 'text-warning' : '',
            ].join(' ')}
          >
            {formatBRL(entry.snapshot.valorBRL)}
          </span>
          <MemoryToggle expanded={expanded} onToggle={() => setExpanded((v) => !v)} numero={numeroLabel} />
          <span className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <RowAction label="Editar" onClick={() => onEdit?.(entry)}>
              <Pencil className="h-3.5 w-3.5" />
            </RowAction>
            <RowAction label="Copiar" onClick={() => onCopy?.(entry)}>
              <Copy className="h-3.5 w-3.5" />
            </RowAction>
            <RowAction label="Recalcular" onClick={() => onRecalc?.(entry)}>
              <RotateCcw className="h-3.5 w-3.5" />
            </RowAction>
            <RowAction label="Excluir" onClick={() => onDelete?.(entry)} destructive>
              <Trash2 className="h-3.5 w-3.5" />
            </RowAction>
          </span>
        </div>
        <EntryMeta entry={entry} isPendente={isPendente} showTipo={showTipo} />
      </div>

      {expanded ? <ExpandedMemory entry={entry} showTipo={showTipo} /> : null}
    </div>
  )
}

function MemoryToggle({
  expanded,
  onToggle,
  numero,
}: {
  expanded: boolean
  onToggle: () => void
  numero: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={`Alternar memória de cálculo do lançamento ${numero}`}
      className={[
        'flex h-7 items-center gap-1 justify-self-start rounded-md px-2 text-[11px] font-medium transition-colors',
        expanded
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
      />
      Memória
    </button>
  )
}

function EntryMeta({
  entry,
  isPendente,
  showTipo,
}: {
  entry: Entry
  isPendente: boolean
  showTipo?: boolean
}) {
  const categoria = entry.categoria ?? 'sem categoria'
  const tag = entry.tag ? `#${entry.tag}` : null
  const indent = showTipo ? 'pl-[124px]' : 'pl-[60px]'

  if (isPendente) {
    const detalhe = entry.snapshot.erro?.detalhe ?? 'cotação ainda não disponível'
    return (
      <div className={`text-muted-foreground mt-1 flex items-center gap-2 ${indent} text-[11px]`}>
        <span aria-hidden>↳</span>
        <span className="select-text">
          {categoria} · {detalhe}
        </span>
      </div>
    )
  }

  return (
    <div className={`text-muted-foreground mt-1 flex items-center gap-2 ${indent} text-[11px]`}>
      <span aria-hidden>↳</span>
      <span className="select-text">
        {categoria}
        {tag ? (
          <>
            {' '}
            · <span className="text-foreground">{tag}</span>
          </>
        ) : null}
        {entry.snapshot.dataPTAXAplicavel ? (
          <>
            {' '}
            · PTAX&nbsp;
            <span className="font-mono">
              {formatStylizedDate(entry.snapshot.dataPTAXAplicavel)}
            </span>
          </>
        ) : null}
        {entry.snapshot.calculadoEm ? (
          <> · há&nbsp;{formatRelativeAgo(entry.snapshot.calculadoEm)}</>
        ) : null}
      </span>
    </div>
  )
}

function RowAction({
  label,
  onClick,
  children,
  destructive,
}: {
  label: string
  onClick?: () => void
  children: React.ReactNode
  destructive?: boolean
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      onClick={onClick}
      className={destructive ? 'h-7 w-7 hover:text-destructive' : 'h-7 w-7'}
    >
      {children}
    </Button>
  )
}

function ExpandedMemory({ entry, showTipo }: { entry: Entry; showTipo?: boolean }) {
  const isCalculated = entry.snapshot.status === 'calculado'
  const indent = showTipo ? 'pl-[124px]' : 'pl-[60px]'
  return (
    <div className={`${indent} pr-6 pb-5 text-[12px] select-text`}>
      <div className="border-l-2 border-border pl-4 ml-2">
        <h4 className="text-muted-foreground text-[10px] font-medium tracking-widest2 uppercase mb-3">
          Memória de cálculo
        </h4>

        <dl className="grid grid-cols-[160px_minmax(0,1fr)] gap-y-2">
          <Term>Data do recebimento</Term>
          <Value>
            <span className="font-mono">{formatStylizedDate(entry.dataRecebimento)}</span>{' '}
            <span className="text-muted-foreground">({weekdayLong(entry.dataRecebimento)})</span>
          </Value>

          <Term>Valor original</Term>
          <Value>
            <span className="font-mono">{formatOrig(entry.valorOriginal)}</span>{' '}
            <span className="font-mono">{entry.moedaOrigem}</span>
          </Value>

          {isCalculated && entry.snapshot.cotacaoMoedaParaUSD !== undefined ? (
            <>
              <Term>Cotação {entry.moedaOrigem}→USD</Term>
              <Value>
                <span className="font-mono">
                  1 {entry.moedaOrigem} = {formatOrig(entry.snapshot.cotacaoMoedaParaUSD)} USD
                </span>
                {entry.snapshot.dataCotacaoMoedaParaUSD ? (
                  <span className="text-muted-foreground ml-2">
                    ({weekdayShort(entry.snapshot.dataCotacaoMoedaParaUSD)}{' '}
                    <span className="font-mono">
                      {formatStylizedDate(entry.snapshot.dataCotacaoMoedaParaUSD)}
                    </span>
                    )
                  </span>
                ) : null}
              </Value>
            </>
          ) : null}

          {isCalculated && entry.snapshot.valorUSD !== undefined ? (
            <>
              <Term>Valor em USD</Term>
              <Value>
                <span className="font-mono">{formatOrig(entry.snapshot.valorUSD)} USD</span>
              </Value>
            </>
          ) : null}

          {isCalculated && entry.snapshot.dataPTAXAplicavel ? (
            <>
              <Term>Data PTAX aplicável</Term>
              <Value>
                <span className="font-mono">
                  {formatStylizedDate(entry.snapshot.dataPTAXAplicavel)}
                </span>{' '}
                <span className="text-muted-foreground">
                  ({weekdayLong(entry.snapshot.dataPTAXAplicavel)})
                </span>
                <p className="text-muted-foreground text-[11px] mt-0.5">
                  último dia útil da 1ª quinzena do mês anterior ao recebimento
                </p>
              </Value>
            </>
          ) : null}

          {isCalculated && entry.snapshot.cotacaoUSDBRL !== undefined ? (
            <>
              <Term>Cotação USD→BRL</Term>
              <Value>
                <span className="font-mono">
                  1 USD = R$ {formatBRL(entry.snapshot.cotacaoUSDBRL)}
                </span>{' '}
                <span className="text-muted-foreground">(PTAX compra)</span>
              </Value>
            </>
          ) : null}

          {isCalculated && entry.snapshot.valorBRL !== undefined ? (
            <>
              <Term>Valor em BRL</Term>
              <Value>
                <span className="font-mono text-[14px] font-medium">
                  R$ {formatBRL(entry.snapshot.valorBRL)}
                </span>
              </Value>
            </>
          ) : null}

          {entry.snapshot.fontes ? (
            <>
              <Term>Fontes</Term>
              <Value>
                <ul className="space-y-0.5 text-[11px]">
                  <li>
                    <span className="text-muted-foreground">BCB SGS</span>{' '}
                    <span className="font-mono">{entry.snapshot.fontes.bcb.serie}</span> —{' '}
                    <span className="font-mono">{entry.snapshot.fontes.bcb.data}</span>
                  </li>
                  {entry.snapshot.fontes.boc ? (
                    <li>
                      <span className="text-muted-foreground">BoC</span>{' '}
                      <span className="font-mono">
                        {entry.snapshot.fontes.boc.grupo}/{entry.snapshot.fontes.boc.serie}
                      </span>{' '}
                      — <span className="font-mono">{entry.snapshot.fontes.boc.data}</span>
                    </li>
                  ) : null}
                </ul>
              </Value>
            </>
          ) : null}

          {entry.snapshot.calculadoEm ? (
            <>
              <Term>Calculado em</Term>
              <Value>
                <span className="font-mono">
                  {new Date(entry.snapshot.calculadoEm).toLocaleString('pt-BR')}
                </span>{' '}
                <span className="text-muted-foreground">
                  (há {formatRelativeAgo(entry.snapshot.calculadoEm)})
                </span>
              </Value>
            </>
          ) : null}

          {!isCalculated && entry.snapshot.erro ? (
            <>
              <Term>Erro</Term>
              <Value>
                <span className="text-warning">
                  {entry.snapshot.erro.codigo} — {entry.snapshot.erro.detalhe}
                </span>
              </Value>
            </>
          ) : null}
        </dl>
      </div>
    </div>
  )
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
      {children}
    </dt>
  )
}

function Value({ children }: { children: React.ReactNode }) {
  return <dd>{children}</dd>
}
